import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isProActive, formatPeriodEnd, startTopup, fetchUsage } from '../billing';
import PageMeta from '../PageMeta';

const check = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);

const INCLUDED = [
  'Full risk breakdown (HIGH / MEDIUM / LOW)',
  'Plan of subdivision with your lot circled',
  'Easements decoded and mapped',
  'Council, water, OC & land tax fees totalled',
  'Five ranked questions for the agent',
  'Works with scanned documents',
];

/* Same pointer-tracking spotlight as the Home page cards. */
function useCardSpotlight() {
  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return undefined;
    const cards = document.querySelectorAll('.pricing-page .pricing-card');
    const move = (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
      e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    cards.forEach(c => c.addEventListener('mousemove', move));
    return () => cards.forEach(c => c.removeEventListener('mousemove', move));
  }, []);
}

export default function Pricing({ plan, billingLive, onPurchase, onCancelPlan }) {
  useCardSpotlight();
  const [processing, setProcessing] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [usage, setUsage] = useState(null);
  const proActive = isProActive(plan);
  const endsOn = plan?.cancelAtPeriodEnd ? formatPeriodEnd(plan.periodEnd) : null;

  // Pro monthly: fetch this month's usage so the banner can show it and the
  // top-up offer can appear once the allowance is used up.
  useEffect(() => {
    if (proActive && plan?.type === 'pro-monthly') {
      fetchUsage(plan).then(setUsage);
    } else {
      setUsage(null);
    }
  }, [plan, proActive]);

  const allowanceUsedUp = !!(usage?.metered && usage.used >= usage.limit);

  async function buy(planType) {
    if (processing) return;
    setProcessing(planType);
    setCheckoutError(null);
    try {
      await onPurchase(planType);
    } catch (err) {
      setCheckoutError(err.message || 'Could not start checkout. Please try again.');
      setProcessing(null);
    }
  }

  async function buyTopup() {
    if (processing) return;
    setProcessing('topup');
    setCheckoutError(null);
    try {
      const url = await startTopup(plan);
      window.location.assign(url);
      await new Promise(r => setTimeout(r, 15000)); // spinner until the page unloads
    } catch (err) {
      setCheckoutError(err.message || 'Could not start checkout. Please try again.');
      setProcessing(null);
    }
  }

  function proButton(planType, label) {
    if (proActive) {
      return <div className="pricing-current">{check} Your current plan family</div>;
    }
    return (
      <button
        className="btn-cta-primary pricing-buy"
        onClick={() => buy(planType)}
        disabled={!!processing || !billingLive}
      >
        {processing === planType ? <span className="paywall-spinner" /> : label}
      </button>
    );
  }

  return (
    <main className="pricing-page">
      <PageMeta
        title="Pricing — Prop Doc | Section 32 reports from $2.99"
        path="/pricing"
        description="One Section 32 report for $2.99, or go Pro from $14.99/month for 20+ reports. Pay only when your analysis is ready — no account needed for a single report."
      />
      <section className="pricing-hero">
        <span className="hero-eyebrow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
          </svg>
          Same full report on every plan
        </span>
        <h1>Pricing that matches <span className="title-grad">how you buy.</span></h1>
        <p>
          One property or a season of Saturday auctions — pay for exactly what you need.
          Every plan gets the same full report.
        </p>
      </section>

      {proActive && (
        <div className="pricing-plan-banner" role="status">
          <span className="site-nav-pro-dot" />
          {endsOn
            ? `Pro is active until ${endsOn}.`
            : `You're on Pro (${plan.type === 'pro-annual' ? 'annual — 200+ reports a year' : 'monthly — 20 reports a month'}).`}
          {usage?.metered && ` ${usage.used} of ${usage.limit} used this month.`}
          {!endsOn && (
            <button className="pricing-cancel" onClick={onCancelPlan}>Cancel plan</button>
          )}
        </div>
      )}

      {allowanceUsedUp && (
        <section className="pricing-topup" aria-label="Extra reports">
          <div>
            <h2>Out of reports this month?</h2>
            <p>
              You've used all {usage.limit} of this month's reports. Add 10 more for $9.99 —
              they're available immediately and last until your allowance resets on the 1st.
            </p>
          </div>
          <button
            className="btn-cta-primary"
            onClick={buyTopup}
            disabled={!!processing || !billingLive}
          >
            {processing === 'topup' ? <span className="paywall-spinner" /> : 'Add 10 reports — $9.99'}
          </button>
        </section>
      )}

      {!billingLive && (
        <div className="pricing-plan-banner" role="status">
          Billing is currently disabled on this server — analysis runs free of charge.
        </div>
      )}

      <section className="pricing-cards">
        <div className="pricing-card">
          <h2>Single report</h2>
          <div className="pricing-amount">$2.99<span> one-off</span></div>
          <p className="pricing-per">One document, one full report</p>
          <p className="pricing-desc">
            For the one property you're serious about. Upload first — you only pay
            once your report is ready to unlock.
          </p>
          <ul className="pricing-features">
            <li>{check} One full report, for this document only</li>
            <li>{check} Pay only after the analysis succeeds</li>
            <li>{check} No account or subscription needed</li>
            <li>{check} Report saved on your device for reopening</li>
            <li>{check} Everything in "Every report includes"</li>
          </ul>
          <Link to="/analyze" className="btn-cta-ghost pricing-buy">Start an analysis</Link>
          <p className="pricing-note">You'll be asked to pay when the report is ready.</p>
        </div>

        <div className="pricing-card pricing-card-hot">
          <span className="price-teaser-badge">Most popular</span>
          <h2>Pro monthly</h2>
          <div className="pricing-amount">$19.99<span>/month</span></div>
          <p className="pricing-per">20 reports a month — about $1 a report</p>
          <p className="pricing-desc">
            Enough for a season of Saturday inspections. No paywall between you
            and your results. Cancel anytime.
          </p>
          <ul className="pricing-features">
            <li>{check} 20 Section 32/Contract of Sale reports per month</li>
            <li>{check} Straight to results — no unlock step</li>
            <li>{check} Ran out? Top up 10 more for $9.99</li>
            <li>{check} Sign in on any device — plan &amp; usage follow you</li>
            <li>{check} Usage meter in My Reports</li>
            <li>{check} Cancel anytime, keep access to period end</li>
          </ul>
          {proButton('pro-monthly', 'Go Pro monthly')}
        </div>

        <div className="pricing-card">
          <h2>Pro annual</h2>
          <div className="pricing-amount">$180<span>/year</span></div>
          <p className="pricing-per">$15 a month, billed once — save 25%</p>
          <p className="pricing-desc">
            For investors, buyer's advocates and anyone who reads contracts
            for a living.
          </p>
          <ul className="pricing-features">
            <li>{check} 200+ Section 32/Contract of Sale reports a year</li>
            <li>{check} Everything in Pro monthly</li>
            <li>{check} Save 25% vs paying monthly</li>
            <li>{check} Sign in on any device — plan follows you</li>
            <li>{check} One receipt for the tax file</li>
          </ul>
          {proButton('pro-annual', 'Go Pro annual')}
        </div>
      </section>

      {checkoutError && <p className="paywall-error pricing-error" role="alert">{checkoutError}</p>}

      <section className="pricing-included">
        <h2>Every report includes</h2>
        <ul>
          {INCLUDED.map(item => <li key={item}>{check} {item}</li>)}
        </ul>
      </section>

      <section className="pricing-faq">
        <details className="faq-item">
          <summary>When do I pay for a single report?</summary>
          <p>
            After the analysis finishes. Upload the document, and when the report is ready
            you'll be asked to unlock it for $2.99 — so you never pay for a failed upload.
          </p>
        </details>
        <details className="faq-item">
          <summary>Can I use Pro on another device?</summary>
          <p>
            Yes — your email is your account. Go to My Reports on the other device,
            enter the email you used at checkout, and click the sign-in link we send.
            Your plan and monthly report count follow you automatically.
          </p>
        </details>
        <details className="faq-item">
          <summary>How do I cancel?</summary>
          <p>
            One click, right on this page (or from My Reports). You keep Pro access until
            the end of the period you've paid for — no phone calls, no forms.
          </p>
        </details>
        <details className="faq-item">
          <summary>Is checkout secure?</summary>
          <p>
            Payments are handled entirely by Stripe — card details never touch our servers.
          </p>
        </details>
      </section>
    </main>
  );
}
